(() => {
  const SELECTOR = '[data-catering-gallery]';

  const initGallery = (gallery) => {
    if (!gallery || gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';

    const layout = gallery.dataset.galleryLayout || 'slider';
    const lightboxEnabled = gallery.dataset.galleryLightbox === 'true';
    const viewport = gallery.querySelector('[data-gallery-viewport]');
    const track = gallery.querySelector('[data-gallery-track]');
    const items = Array.from(gallery.querySelectorAll('[data-catering-gallery-item]'));
    const previousButton = gallery.querySelector('[data-gallery-prev]');
    const nextButton = gallery.querySelector('[data-gallery-next]');
    const dotsContainer = gallery.querySelector('[data-gallery-dots]');
    const section = gallery.closest('.hindi-catering');
    const modal = section?.querySelector('[data-catering-lightbox-modal]');

    if (!viewport || !track || !items.length) return;

    let currentPage = 0;
    let pages = 1;
    let perPage = 1;
    let pointerStartX = null;
    let pointerMoved = false;
    let lightboxIndex = 0;
    let lastFocusedElement = null;

    const getPerPage = () => {
      if (window.innerWidth >= 990) return 3;
      if (window.innerWidth >= 750) return 2;
      return 1;
    };

    const updateDots = () => {
      if (!dotsContainer || layout !== 'slider') return;
      dotsContainer.innerHTML = '';
      for (let index = 0; index < pages; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hindi-catering__gallery-dot';
        dot.setAttribute('aria-label', `Go to gallery page ${index + 1}`);
        dot.setAttribute('aria-current', index === currentPage ? 'true' : 'false');
        dot.addEventListener('click', () => {
          currentPage = index;
          updateSlider();
        });
        dotsContainer.appendChild(dot);
      }
    };

    const updateSlider = () => {
      if (layout !== 'slider') return;

      perPage = getPerPage();
      pages = Math.max(1, Math.ceil(items.length / perPage));
      currentPage = Math.min(currentPage, pages - 1);

      const maxStartIndex = Math.max(0, items.length - perPage);
      const startIndex = Math.min(currentPage * perPage, maxStartIndex);
      const target = items[startIndex];
      const offset = target ? target.offsetLeft : 0;
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      if (previousButton) previousButton.disabled = currentPage === 0;
      if (nextButton) nextButton.disabled = currentPage >= pages - 1;

      if (!dotsContainer || dotsContainer.children.length !== pages) {
        updateDots();
      } else {
        Array.from(dotsContainer.children).forEach((dot, index) => {
          dot.setAttribute('aria-current', index === currentPage ? 'true' : 'false');
        });
      }
    };

    const movePage = (direction) => {
      if (layout !== 'slider') return;
      currentPage = Math.max(0, Math.min(pages - 1, currentPage + direction));
      updateSlider();
    };

    previousButton?.addEventListener('click', () => movePage(-1));
    nextButton?.addEventListener('click', () => movePage(1));

    viewport.addEventListener('pointerdown', (event) => {
      if (layout !== 'slider') return;
      pointerStartX = event.clientX;
      pointerMoved = false;
      viewport.setPointerCapture?.(event.pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (pointerStartX === null) return;
      if (Math.abs(event.clientX - pointerStartX) > 10) pointerMoved = true;
    });

    viewport.addEventListener('pointerup', (event) => {
      if (pointerStartX === null || layout !== 'slider') return;
      const delta = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(delta) > 45) movePage(delta < 0 ? 1 : -1);
    });

    const modalImage = modal?.querySelector('[data-lightbox-image]');
    const modalCaption = modal?.querySelector('[data-lightbox-caption]');
    const closeButtons = modal ? Array.from(modal.querySelectorAll('[data-lightbox-close]')) : [];
    const lightboxPrevious = modal?.querySelector('[data-lightbox-prev]');
    const lightboxNext = modal?.querySelector('[data-lightbox-next]');

    const renderLightbox = () => {
      if (!modal || !modalImage) return;
      const item = items[lightboxIndex];
      const image = item?.querySelector('img');
      if (!item || !image) return;
      modalImage.src = item.href;
      modalImage.alt = image.alt || '';
      if (modalCaption) modalCaption.textContent = image.alt || '';
    };

    const openLightbox = (index) => {
      if (!lightboxEnabled || !modal) return;
      lightboxIndex = index;
      lastFocusedElement = document.activeElement;
      renderLightbox();
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('hindi-catering-lightbox-open');
      modal.querySelector('.hindi-catering-lightbox__close')?.focus();
    };

    const closeLightbox = () => {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('hindi-catering-lightbox-open');
      if (modalImage) modalImage.src = '';
      lastFocusedElement?.focus?.();
    };

    const moveLightbox = (direction) => {
      lightboxIndex = (lightboxIndex + direction + items.length) % items.length;
      renderLightbox();
    };

    items.forEach((item, index) => {
      item.addEventListener('click', (event) => {
        if (pointerMoved) {
          event.preventDefault();
          pointerMoved = false;
          return;
        }
        if (!lightboxEnabled) return;
        event.preventDefault();
        openLightbox(index);
      });
    });

    closeButtons.forEach((button) => button.addEventListener('click', closeLightbox));
    lightboxPrevious?.addEventListener('click', () => moveLightbox(-1));
    lightboxNext?.addEventListener('click', () => moveLightbox(1));

    modal?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft') moveLightbox(-1);
      if (event.key === 'ArrowRight') moveLightbox(1);
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(updateSlider, 120);
    }, { passive: true });

    if (layout === 'slider') updateSlider();
  };

  const initAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(initGallery);
  };

  const start = () => initAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
